import React, { useState, useEffect } from 'react';
import Slidebar from './Slidebar';
import apiInstance from '../../utils/axios';
import Swal from 'sweetalert2';
import UserData from '../plugin/UserData';
import moment from 'moment';
import {
    Bell,
    BellOff,
    Check,
    CheckCircle,
    Clock,
    RefreshCw,
    Package,
    ShoppingBag,
    Eye
} from 'lucide-react';


function CustomerNotifications() {
    
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingId, setMarkingId] = useState(null);

    const fetchNotifications = () => {
        setLoading(true);
        if (UserData()?.user_id) {
            apiInstance.get(`customer/notification/${UserData().user_id}/`).then((res) => {
                setNotifications(res.data);
                setLoading(false);
            }).catch((error) => {
                console.error('Error fetching notifications:', error);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchNotifications();
    }, []);

    const MarkNotificationsAsSeen = (notification_id) => {
        setMarkingId(notification_id);
        apiInstance.get(`customer/notification/${UserData().user_id}/${notification_id}/`).then((res) => {
            setNotifications(prevNotifications => 
                prevNotifications.filter(notification => notification.id !== notification_id)
            );
            
            Swal.fire({
                icon: 'success',
                title: "Notificación marcada como vista",
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
            setMarkingId(null);
        }).catch((error) => {
            console.error('Error marking notification as seen:', error);
            Swal.fire({
                icon: 'error',
                title: "Error",
                text: "Error al marcar la notificación como vista",
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            setMarkingId(null);
        });
    }

    return (
        <div className="notifications-page">
            <div className="notifications-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner" />
                        <p>Cargando notificaciones...</p>
                    </div>
                ) : (
                    <div className="notifications-layout">
                        <aside className="notifications-sidebar">
                            <Slidebar />
                        </aside>

                        <main className="notifications-main">
                            {/* Header */}
                            <div className="section-header">
                                <div className="header-info">
                                    <h2>
                                        <Bell size={22} />
                                        Todas las Notificaciones
                                    </h2>
                                    {notifications.length > 0 && (
                                        <span className="count-badge">{notifications.length}</span>
                                    )}
                                </div>
                                <button 
                                    className="refresh-btn" 
                                    onClick={() => fetchNotifications()}
                                >
                                    <RefreshCw size={18} />
                                    Actualizar
                                </button>
                            </div>

                            {notifications.length > 0 ? (
                                <div className="notifications-list">
                                    {notifications.map((n, index) => (
                                        <div 
                                            key={n.id} 
                                            className={`notification-card ${markingId === n.id ? 'marking' : ''}`}
                                            style={{ animationDelay: `${index * 0.05}s` }}
                                        >
                                            <div className="notification-icon">
                                                <CheckCircle size={24} />
                                            </div>
                                            
                                            <div className="notification-content">
                                                <div className="notification-header">
                                                    <h4>¡Pedido Confirmado!</h4>
                                                    <span className="notification-time">
                                                        <Clock size={14} />
                                                        {moment(n.date).format("MMM D, YYYY - h:mm A")}
                                                    </span>
                                                </div>
                                                <p className="notification-message">
                                                    Tu pedido ha sido confirmado y está siendo procesado. 
                                                    Recibirás actualizaciones sobre el estado de tu pedido.
                                                </p>
                                                <div className="notification-meta">
                                                    <span className="meta-item">
                                                        <Package size={14} />
                                                        Pedido #{n.order?.oid || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                className="mark-seen-btn"
                                                onClick={() => MarkNotificationsAsSeen(n.id)}
                                                disabled={markingId === n.id}
                                            >
                                                {markingId === n.id ? (
                                                    <div className="mini-spinner" />
                                                ) : (
                                                    <>
                                                        <Eye size={16} />
                                                        <span>Marcar como visto</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <BellOff size={48} />
                                    </div>
                                    <h3>Sin notificaciones</h3>
                                    <p>¡Estás al día! Vuelve más tarde para ver actualizaciones.</p>
                                    <button className="refresh-empty-btn" onClick={() => fetchNotifications()}>
                                        <RefreshCw size={18} />
                                        Actualizar
                                    </button>
                                </div>
                            )}
                        </main>
                    </div>
                )}
            </div>

            <style>{`
                .notifications-page {
                    background: #ffffff;
                    min-height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                /* Layout */
                .notifications-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 32px 24px 60px;
                }

                .notifications-layout {
                    display: flex;
                    gap: 32px;
                }

                .notifications-sidebar {
                    width: 320px;
                    flex-shrink: 0;
                }

                .notifications-main {
                    flex: 1;
                    min-width: 0;
                }

                /* Loading */
                .loading-state {
                    text-align: center;
                    padding: 80px 20px;
                }

                .spinner {
                    width: 48px;
                    height: 48px;
                    border: 4px solid #e2e8f0;
                    border-top-color: #f59e0b;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 20px;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Section Header */
                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }

                .header-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .header-info h2 {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 20px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                }

                .count-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 28px;
                    height: 28px;
                    padding: 0 10px;
                    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
                    color: white;
                    font-size: 13px;
                    font-weight: 700;
                    border-radius: 14px;
                }

                .refresh-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .refresh-btn:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }

                /* Notifications List */
                .notifications-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .notification-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 20px;
                    padding: 24px;
                    background: white;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
                    transition: all 0.3s ease;
                    animation: slideIn 0.3s ease forwards;
                    opacity: 0;
                    transform: translateY(10px);
                }

                @keyframes slideIn {
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .notification-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
                }

                .notification-card.marking {
                    opacity: 0.6;
                    transform: scale(0.98);
                }

                .notification-icon {
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #10b981;
                    flex-shrink: 0;
                }

                .notification-content {
                    flex: 1;
                    min-width: 0;
                }

                .notification-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 8px;
                    gap: 12px;
                }

                .notification-header h4 {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                }

                .notification-time {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    color: #94a3b8;
                    flex-shrink: 0;
                }

                .notification-message {
                    font-size: 14px;
                    color: #64748b;
                    line-height: 1.5;
                    margin: 0 0 12px;
                }

                .notification-meta {
                    display: flex;
                    gap: 16px;
                }

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    color: #64748b;
                }

                .mark-seen-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 20px;
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    color: #059669;
                    border: none;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }

                .mark-seen-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                }

                .mark-seen-btn:disabled {
                    opacity: 0.7;
                    cursor: wait;
                }

                .mini-spinner {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(5, 150, 105, 0.3);
                    border-top-color: #059669;
                    border-radius: 50%;
                    animation: spin 0.6s linear infinite;
                }

                /* Empty State */
                .empty-state {
                    text-align: center;
                    padding: 80px 20px;
                    background: white;
                    border-radius: 24px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
                }

                .empty-icon {
                    width: 96px;
                    height: 96px;
                    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                    border-radius: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 24px;
                    color: #94a3b8;
                }

                .empty-state h3 {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 8px;
                }

                .empty-state p {
                    font-size: 15px;
                    color: #64748b;
                    margin: 0 0 24px;
                }

                .refresh-empty-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 14px 28px;
                    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .refresh-empty-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
                }

                /* Responsive */
                @media (max-width: 1024px) {
                    .notifications-sidebar {
                        display: none;
                    }
                }

                @media (max-width: 768px) {
                    .notifications-container {
                        padding: 20px 16px 40px;
                    }

                    .notification-card {
                        flex-direction: column;
                        gap: 16px;
                    }

                    .notification-header {
                        flex-direction: column;
                        gap: 8px;
                    }

                    .mark-seen-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .section-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .refresh-btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
}

export default CustomerNotifications;